import type { QueryKey } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";

import { QueryClient } from "@citrine/client";
import { getLookupMap } from "@citrine/utils/helpers";

import type {
  IAttributeTemplatePair,
  IDataObjectOrTemplate,
  IMaterialRun,
  OptionallyScopedIdentifier,
} from "../data-object";
import {
  isAttributeTemplate,
  isLinkUid,
  isObjectTemplate,
} from "../data-object";
import type { GemdCriterium } from "../table-config";

interface MaterialHistoriesPayload {
  context: IDataObjectOrTemplate[];
  roots: IMaterialRun[];
}

export interface IMaterialHistoryResponse {
  context: IDataObjectOrTemplate[];
  root: IMaterialRun;
}

function getBoundsRemapper(
  historyMap: Partial<Record<string, IDataObjectOrTemplate>>
) {
  return function remapPassthroughBounds<Pair extends IAttributeTemplatePair>(
    pair: Pair
  ): Pair {
    const attributeTemplate = pair[0];
    if (isLinkUid(attributeTemplate)) {
      const template = historyMap[attributeTemplate.id];
      if (isAttributeTemplate(template) && pair[1] === null) {
        pair[1] = template.bounds;
      }
      return pair;
    }
    // this fallback section never happens with the current material history
    // the template is always a link_by_uid, but in case it changes, this code is
    // present
    if (pair[1] === null) {
      pair[1] = attributeTemplate.bounds;
    }
    return pair;
  };
}

export class MaterialHistoryClient extends QueryClient<
  IMaterialHistoryResponse,
  OptionallyScopedIdentifier
> {
  create: never;
  delete: never;
  find: never;
  update: never;
  upsert: never;

  useClone: never;
  useCreate: never;
  useDelete: never;
  useFind: never;
  useFindAll: never;
  useMap: never;
  useUpdate: never;
  useUpsert: never;

  protected constructor(
    teamId: string,
    protected readonly datasetId?: string
  ) {
    super({
      endpoint: `/api/v1/teams/${teamId}/gemd/query/material-histories`,
      getField: "0", // the API returns an array
    });
  }

  static getInstance(teamId: string, datasetId?: string) {
    return new MaterialHistoryClient(teamId, datasetId);
  }

  getModelCacheKey(id?: OptionallyScopedIdentifier): QueryKey {
    return [this.getUri(id)];
  }

  getUri(id?: OptionallyScopedIdentifier) {
    return typeof id === "string"
      ? `${this.endpoint}/id/${id}`
      : `${this.endpoint}/${id?.scope || "id"}/${id?.id}`;
  }

  getId(model: IMaterialHistoryResponse) {
    return model.root.uids.id;
  }

  protected toModel(
    payload: IMaterialHistoryResponse | MaterialHistoriesPayload
  ): IMaterialHistoryResponse {
    const historyMap = getLookupMap(payload.context, (d) => d.uids.id);
    const boundsRemapper = getBoundsRemapper(historyMap);

    // iterate and find all objects that need to have their pass-through bounds adjusted
    return {
      root: {
        ...("root" in payload ? payload.root : payload.roots[0]),
        persisted: true,
        // this field is not provided by the back-end, but we need it
        type: "material_run",
      },
      context: payload.context.map((dataObject) => {
        dataObject.persisted = true;
        if (isObjectTemplate(dataObject)) {
          dataObject.properties = dataObject.properties?.map(boundsRemapper);
          dataObject.parameters = dataObject.parameters?.map(boundsRemapper);
          dataObject.conditions = dataObject.conditions?.map(boundsRemapper);
        }
        return dataObject;
      }),
    };
  }

  protected getConfig(id: OptionallyScopedIdentifier): AxiosRequestConfig {
    return {
      method: "POST",
      url: this.endpoint,
      params: {
        filter_nonroot_materials: "true",
      },
      data: {
        datasets: this.datasetId ? [this.datasetId] : undefined,
        criteria: [
          {
            type: "terminal_material_run_identifiers_criteria",
            terminal_material_ids: [
              typeof id === "string"
                ? {
                    scope: "id",
                    id,
                  }
                : id,
            ],
          },
        ] as GemdCriterium[],
      },
    };
  }
}
